import { useState } from 'react';
import { Box, Button, CircularProgress, Container, Typography, Chip, Avatar, Autocomplete, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { useEventDetails } from '../context';
import NoEventError from './NoEventError';
import { queryAuthorizedUsersWithPermissions, queryCurrentUser } from '../schema';
import { db } from '../instant';
import GenerateQRDialog from './GenerateQRDialog';
import UserPermissionsModal from './UserPermissionsModal';
import { PERMISSION_LABELS } from '../constants/permissions';
import { EVENT_TAB_SCROLL_END_GAP_PX } from 'src/spacing';

interface Props {
    eventId: string;
}

interface UserEventAccess {
    hasAccess: boolean;
    isAll: boolean;
}

export default function EventUsers(props: Props) {
    const { event, isLoading, error } = useEventDetails(props.eventId);
    const { data: userData, isLoading: usersLoading } = queryAuthorizedUsersWithPermissions();
    const { data: currentUserData } = queryCurrentUser();
    const [qrDialogOpen, setQrDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any | null>(null);

    if (isLoading || usersLoading) {
        return (
            <Box display="flex" justifyContent="center" padding={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Typography color="error">Error loading event details: {error.message}</Typography>;
    }

    if (!event) {
        return <NoEventError eventId={props.eventId} />;
    }

    const users = userData?.$users || [];
    const currentUser = currentUserData?.$users?.[0];

    // Check if current user has admin or editUsers permission
    const isAdmin = currentUser?.admin || currentUser?.editUsers || false;

    const getUserEventAccess = (user: any): UserEventAccess => {
        if (!user.allowedEventIds) {
            return { hasAccess: false, isAll: false };
        }

        const allowedEventIds = user.allowedEventIds;

        if (allowedEventIds === 'all') {
            return { hasAccess: true, isAll: true };
        }

        try {
            const parsedIds = JSON.parse(allowedEventIds);
            return {
                hasAccess: parsedIds.includes(props.eventId),
                isAll: false,
            };
        } catch {
            return { hasAccess: false, isAll: false };
        }
    };

    const updateUserEventAccess = async (userId: string, hasAccess: boolean) => {
        try {
            const user = users.find(u => u.id === userId);
            if (!user) return;

            if (user.allowedEventIds === 'all') {
                // Can't remove from "all"; would need to set explicit list
                if (!hasAccess) return;
                return;
            }

            let newAllowedEventIds: string | undefined;

            if (hasAccess) {
                // Add this event
                let newIds = [props.eventId];
                if (user.allowedEventIds) {
                    try {
                        const parsedIds: string[] = JSON.parse(user.allowedEventIds);
                        newIds = [...parsedIds, props.eventId];
                    } catch {
                        newIds = [props.eventId];
                    }
                }
                newAllowedEventIds = JSON.stringify(newIds);
            } else {
                // Remove this event
                if (user.allowedEventIds) {
                    try {
                        const parsedIds: string[] = JSON.parse(user.allowedEventIds);
                        const updatedIds = parsedIds.filter(id => id !== props.eventId);
                        newAllowedEventIds = updatedIds.length > 0 ? JSON.stringify(updatedIds) : undefined;
                    } catch {
                        newAllowedEventIds = undefined;
                    }
                }
            }

            await db.transact(
                db.tx.$users[userId].update({
                    allowedEventIds: newAllowedEventIds ?? null,
                })
            );
        } catch (err) {
            console.error('Failed to update user event access:', err);
        }
    };

    const handleToggleUserAccess = async (userId: string, currentAccess: UserEventAccess) => {
        if (currentAccess.isAll) return;
        await updateUserEventAccess(userId, !currentAccess.hasAccess);
    };

    const usersWithAccess = users
        .filter(user => {
            const access = getUserEventAccess(user);
            return access.hasAccess;
        })
        .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));

    const selectedUserIds = usersWithAccess.map(u => u.id);

    const handleUsersMultiSelectChange = async (newValue: string[]) => {
        const prev = selectedUserIds;
        const added = newValue.filter(id => !prev.includes(id));
        const removed = prev.filter(id => !newValue.includes(id));
        for (const id of removed) {
            const u = users.find(u => u.id === id);
            if (u && getUserEventAccess(u).isAll) continue;
            await updateUserEventAccess(id, false);
        }
        for (const id of added) {
            await updateUserEventAccess(id, true);
        }
    };

    const getInitials = (user: any): string => {
        const name = user.name || user.email;
        if (!name) return '?';

        const parts = name.split(' ').filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const stringToColor = (str: string): string => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        const hue = hash % 360;
        return `hsl(${hue}, 65%, 50%)`;
    };

    return (
        <Container
            maxWidth="md"
            disableGutters
            sx={{
                display: 'flex',
                flexGrow: 1,
                flexDirection: 'column',
                paddingBottom: `${EVENT_TAB_SCROLL_END_GAP_PX}px`,
                px: { xs: 2, sm: 3 },
                minHeight: 0,
                maxHeight: '100%',
            }}
        >
            <Typography
                variant="h5"
                sx={{
                    mt: 2,
                    mb: 1.5,
                    fontWeight: 500,
                    flexShrink: 0,
                }}
            >
                Event Users
            </Typography>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2,
                    flexShrink: 0,
                }}
            >
                <Button variant="contained" startIcon={<QrCodeIcon />} onClick={() => setQrDialogOpen(true)}>
                    Invite User
                </Button>
            </Box>
            {isAdmin && (
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={[...users].sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''))}
                    value={usersWithAccess}
                    onChange={(_, newValue) => handleUsersMultiSelectChange(newValue.map(u => u.id))}
                    getOptionLabel={u => u.name || u.email || ''}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderOption={(props, option) => (
                        <li
                            {...props}
                            key={option.id}
                            onMouseDown={e => {
                                e.preventDefault();
                                props.onMouseDown?.(e);
                            }}
                        >
                            {option.name || option.email}
                        </li>
                    )}
                    renderInput={params => <TextField {...params} label="Users with access" size="small" />}
                    sx={{ mb: 2, flexShrink: 0 }}
                    fullWidth
                />
            )}
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                {usersWithAccess.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                        No users have access to this event
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            mt: 1,
                        }}
                    >
                        {usersWithAccess.map(user => {
                            const access = getUserEventAccess(user);
                            const userName = user.name || user.email;
                            const initials = getInitials(user);
                            const avatarColor = stringToColor(userName);

                            return (
                                <Chip
                                    key={user.id}
                                    avatar={
                                        <Avatar
                                            sx={{
                                                bgcolor: avatarColor,
                                                fontSize: '0.875rem',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {initials}
                                        </Avatar>
                                    }
                                    label={userName}
                                    onClick={() => setSelectedUser(user)}
                                    onDelete={isAdmin && !access.isAll ? () => handleToggleUserAccess(user.id, access) : undefined}
                                    deleteIcon={<CloseIcon />}
                                    sx={{
                                        height: 'auto',
                                        py: 1,
                                        '& .MuiChip-label': {
                                            px: 1.5,
                                            fontSize: '0.9rem',
                                        },
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                    }}
                                />
                            );
                        })}
                    </Box>
                )}
            </Box>

            <GenerateQRDialog
                open={qrDialogOpen}
                onClose={() => setQrDialogOpen(false)}
                events={[]}
                permissionLabels={PERMISSION_LABELS}
                preSelectedEvent={event || undefined}
            />

            <UserPermissionsModal open={selectedUser !== null} onClose={() => setSelectedUser(null)} user={selectedUser} />
        </Container>
    );
}
